// ==================================================
// FILE: themes/vanillaTheme.js
// ==================================================

import { 
  Play, 
  Save, 
  Settings, 
  User, 
  Newspaper, 
  Crown, 
  Coffee,
  Home,
  Users,
  FlaskConical,
  Target
} from 'lucide-react';

const vanillaTheme = {
  id: 'vanilla',
  
  // Landing Page Configuration
  title: {
    line1: 'INFINITE',
    line2: 'SQUAD',
    line1Color: 'text-blue-400',
    line2Color: 'text-red-400'
  },
  subtitle: 'TACTICAL SQUAD COMMAND SYSTEM',
  
  colors: {
    primary: 'blue',
    secondary: 'red',
    accent: 'cyan',
    background: 'from-gray-900 via-gray-800 to-black'
  },
  
  backgroundImage: {
    url: '/images/military-background.jpg',
    opacity: 0.15,
    blend: 'multiply',
    position: 'center',
    size: 'cover'
  },
  
  userTitle: 'COMMANDER',
  userTitleGuest: 'GUEST MODE',
  clearanceLevel: 'COMMANDER',
  
  alert: {
    text: 'THREAT LEVEL: CRITICAL - BASE OPERATIONS REQUIRED',
    color: 'red'
  },
  
  menuItems: [
    {
      id: 'new',
      icon: Play,
      label: 'NEW CAMPAIGN',
      subtitle: 'Establish your tactical base',
      color: 'red'
    },
    {
      id: 'load',
      icon: Save,
      label: 'CONTINUE CAMPAIGN',
      subtitle: 'Resume operations from base',
      color: 'blue'
    }
  ],
  
  secondaryItems: [
    { id: 'settings', icon: Settings, label: 'SETTINGS' },
    { id: 'profile', icon: User, label: 'PROFILE' },
    { id: 'news', icon: Newspaper, label: 'INTEL FEED' },
    { id: 'leaderboard', icon: Crown, label: 'LEADERBOARD' }
  ],
  
  support: {
    title: 'SUPPORT THE MISSION',
    description: 'Your support fuels development and keeps our tactical operations running.\nHelp us build the ultimate squad command experience.',
    buttonText: 'FUND OPERATIONS',
    icon: Coffee
  },
  
  footer: {
    status: 'OPERATIONAL',
    readyText: 'READY FOR DEPLOYMENT',
    signInText: 'SIGN IN REQUIRED'
  },
  
  // Base Operations Configuration
  loading: 'LOADING BASE OPERATIONS...',
  baseTitle: 'BASE OPERATIONS',
  startDate: '2025-01-01',
  
  tabs: {
    base: 'BASE',
    squad: 'SQUAD',
    research: 'RESEARCH',
    missions: 'MISSIONS'
  },
  
  icons: {
    base: Home,
    squad: Users,
    research: FlaskConical,
    missions: Target
  },
  
  // Resource labels (optional customization)
  resources: {
    funds: 'FUNDS',
    supplies: 'SUPPLIES',
    intel: 'INTEL',
    power: 'POWER'
  },
  
  // Facility names
  facilities: {
    command: 'Command Center',
    barracks: 'Barracks',
    generator: 'Generator',
    medbay: 'Med Bay',
    research: 'Research Lab',
    workshop: 'Workshop'
  },
  
  // Squad Configuration
  squad: {
    title: 'Squad Roster',
    personnelLabel: 'Active Personnel',
    recruitButton: 'RECRUIT SOLDIER',
    recruitCost: 1000,
    firstRecruitButton: 'RECRUIT FIRST SOLDIER',
    emptyTitle: 'No Squad Members',
    emptyMessage: 'Recruit your first soldier to begin building your squad',
    manageButton: 'MANAGE',
    healButton: 'HEAL',
    healthLabel: 'HEALTH',
    statusLabel: 'STATUS',
    missionsLabel: 'Missions Completed',
    experienceLabel: 'XP',
    summaryTitle: 'Squad Summary',
    statusLabels: {
      ready: 'Ready',
      wounded: 'Wounded',
      training: 'Training',
      onMission: 'On Mission'
    }
  },
  
// ==================================================
// VANILLA THEME - Research Configuration
// ==================================================
research: {
  title: 'Research & Development',
  subtitle: 'Unlock new technologies and enhance your capabilities',
  
  // Tier Labels
  earlyTier: 'TIER 1: FOUNDATION',
  midTier: 'TIER 2: SPECIALIZATION',
  lateTier: 'TIER 3: CUTTING EDGE',
  
  // Tier Descriptions
  earlyDesc: 'Basic technologies to establish your operations',
  midDesc: 'Advanced systems and tactical improvements',
  lateDesc: 'Experimental technology and ultimate power',
  
  // Status Labels
  researchingLabel: 'RESEARCHING',
  prerequisitesLabel: 'PREREQUISITES',
  benefitsLabel: 'BENEFITS',
  unlocksLabel: 'UNLOCKS',
  
  // Action Buttons
  startButton: 'START RESEARCH',
  insufficientButton: 'INSUFFICIENT RESOURCES',
  cancelButton: 'CANCEL',
  
  // Progress Stats
  totalCompletedLabel: 'Total Completed',
  currentResearchLabel: 'Current Research',
  availableProjectsLabel: 'Available Projects',
  ofLabel: 'of',
  projectsLabel: 'projects',
  noneActiveLabel: 'None active',
  readyToResearchLabel: 'Ready to research',
  completedLabel: 'completed',
  availableLabel: 'Available',
  
  // Status Messages
  notAvailableLabel: 'NOT AVAILABLE',
  completedTag: 'COMPLETED',
  lockedTag: 'LOCKED',
  inProgressTag: 'IN PROGRESS'
},
  
  // Missions Configuration
  missions: {
    title: 'Mission Control',
    subtitle: 'Deploy your squad on tactical operations',
    threatLabel: 'THREAT LEVEL',
    activeLabel: 'Active Missions',
    deployedLabel: 'soldiers deployed',
    successLabel: 'SUCCESS',
    failedLabel: 'FAILED',
    simpleTier: 'SIMPLE MISSIONS',
    complexTier: 'COMPLEX MISSIONS',
    campaignTier: 'CAMPAIGN MISSIONS',
    autoOnly: 'AUTO ONLY',
    autoOrManual: 'AUTO OR MANUAL',
    manualOnly: 'MANUAL ONLY',
    squadRequirement: 'SQUAD REQUIREMENT',
    soldiersLabel: 'soldiers',
    unlocksLabel: 'Unlocks on Day',
    insufficientSquad: 'Insufficient ready soldiers',
    minutesLabel: 'minutes',
    rewardsLabel: 'REWARDS',
    risksLabel: 'RISKS',
    casualtyLabel: 'Casualty',
    chooseModeLabel: 'Choose Mission Mode',
    autoResolveTitle: 'AUTO RESOLVE',
    autoResolveDesc: 'Mission resolves automatically based on squad stats. Faster but less control.',
    manualControlTitle: 'MANUAL CONTROL',
    manualControlDesc: 'Full tactical control on the battlefield. Higher risk, higher reward.',
    selectSquadLabel: 'Select Squad Members',
    requiredLabel: 'required',
    noSoldiersLabel: 'No soldiers available for deployment',
    deployAutoButton: 'DEPLOY (AUTO)',
    deployManualButton: 'DEPLOY TO BATTLE',
    cancelButton: 'CANCEL',
    simpleNames: {
      supply: 'Supply Run',
      patrol: 'Patrol Route',
      training: 'Training Exercise'
    },
    complexNames: {
      raid: 'Warehouse Raid',
      assassination: 'HVT Assassination',
      sabotage: 'Sabotage Mission',
      rescue: 'Hostage Rescue'
    },
    campaignNames: {
      vanguard: 'Operation Vanguard',
      liberation: 'Liberation Day'
    }
  },
  
  // Mission Briefing Configuration
  missionBriefing: {
    title: 'MISSION BRIEFING',
    alertText: 'PREPARE FOR DEPLOYMENT - EQUIPMENT CHECK REQUIRED',
    abortButton: 'ABORT MISSION',
    
    intel: {
      title: 'Mission Intel',
      locationLabel: 'LOCATION',
      durationLabel: 'ESTIMATED DURATION',
      threatLabel: 'THREAT ASSESSMENT',
      briefLabel: 'MISSION BRIEF',
      intelligenceLabel: 'INTELLIGENCE REPORT',
      objectivesLabel: 'OBJECTIVES',
      rewardsLabel: 'EXPECTED REWARDS'
    },
    
    roster: {
      title: 'Deployment Roster',
      healthLabel: 'HP'
    },
    
    loadout: {
      title: 'Equipment Loadout',
      selectPrompt: 'SELECT A SOLDIER',
      selectDesc: 'Choose a soldier from the roster to customize their loadout',
      statusLabel: 'COMBAT READY',
      weaponSection: 'Primary Weapon',
      armorSection: 'Body Armor',
      utilitySection: 'Utility Item',
      damageLabel: 'DMG',
      accuracyLabel: 'ACC',
      defenseLabel: 'DEF',
      mobilityLabel: 'MOB'
    },
    
    deploy: {
      readyTitle: 'READY TO DEPLOY',
      readyDesc: 'equipped and ready for combat',
      beginButton: 'BEGIN MISSION',
      warningText: '⚠️ WARNING: Mission cannot be aborted once combat begins. Casualties may occur.'
    }
  }
};

export default vanillaTheme;